import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { masterDataAPI, productMasterAPI } from "../services/api";
import Swal from "sweetalert2";
import $ from "jquery";
import "select2";
import "select2/dist/css/select2.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./RaiseComplaint.css";
import "./AddEditProductMaster.css";
import { getRoutePermissions } from "../utils/auth";

const normalizeCategory = (item) => ({
  catgCode: item.catgCode || item.CATGCODE || item.catgcode || "",
  catgName: item.catgName || item.CATGNAME || item.catgname || "",
});


const normalizeBV = (item) => ({
  bvCode: item.bvCode || item.BVCode || item.BVCODE || "",
  bvName: item.bvName || item.BVName || item.BVNAME || "",
});

const normalizePlant = (item) => ({
  plantCode: item.plantCode || item.PlantCode || item.PLANTCODE || "",
  plantName: item.plantName || item.PlantName || item.PLANTNAME || "",
});

const normalizeProduct = (item) => ({
  productCode: item.productCode || item.ProductCode || "",
  productId: item.productId || item.ProductId || "",
  productName: item.productName || item.ProductName || "",
  catgCode: item.catgCode || item.CATGCODE || "",
  bvCode: item.bvCode || item.BVCODE || "",
  plantCode: item.plantCode || item.PLANTCODE || "",
  isActive: item.isActive ?? item.ISACTIVE ?? true,
});

const AddEditProductMaster = () => {
  const navigate = useNavigate();
  const { productCode } = useParams();
  const isEditMode = !!productCode;

  const routePermissions = getRoutePermissions("/products");
  const { canAdd, canEdit } = routePermissions;
  const canSave = isEditMode ? canEdit : canAdd;

  const [product, setProduct] = useState({
    productCode: "",
    productId: "",
    productName: "",
    catgCode: "",
    bvCode: "",
    plantCode: "",
    isActive: true,
  });

  const [dropdowns, setDropdowns] = useState({
    categories: [],
    businessVerticals: [],
    plants: [],
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const categorySelectRef = useRef(null);
  const bvSelectRef = useRef(null);
  const plantSelectRef = useRef(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    if (!canSave) {
      Swal.fire({
        title: "Access Denied",
        text: `You do not have permission to ${isEditMode ? "edit" : "add"} products.`,
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => navigate("/products"));
      return;
    }
    loadDropdowns();
    if (isEditMode) {
      loadProduct();
    } else {
      // Auto-generate Product Code for new products
      loadNextProductCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productCode, canSave]);

  // Initialize Select2 for dropdowns
  useEffect(() => {
    if (
      dropdowns.categories.length === 0 ||
      dropdowns.businessVerticals.length === 0 ||
      dropdowns.plants.length === 0
    ) {
      return;
    }

    // Initialize Category Select2
    if (categorySelectRef.current) {
      const $categorySelect = $(categorySelectRef.current);
      if ($categorySelect.data("select2")) {
        $categorySelect.select2("destroy");
      }
      $categorySelect.select2({
        width: "100%",
        placeholder: "Select Category",
        allowClear: false,
      });
      $categorySelect.on("select2:select", function (e) {
        const newCatgCode = e.params.data.id;
        handleChange("catgCode", newCatgCode);
      });
      if (product.catgCode) {
        $categorySelect.val(product.catgCode).trigger("change");
      }
    }


    // Initialize Product Vertical Select2
    if (bvSelectRef.current) {
      const $bvSelect = $(bvSelectRef.current);
      if ($bvSelect.data("select2")) {
        $bvSelect.select2("destroy");
      }
      $bvSelect.select2({
        width: "100%",
        placeholder: "Select Product Vertical",
        allowClear: false,
      });
      $bvSelect.on("select2:select", function (e) {
        handleChange("bvCode", e.params.data.id);
      });
      if (product.bvCode) {
        $bvSelect.val(product.bvCode).trigger("change");
      }
    }

    // Initialize Plant Select2
    if (plantSelectRef.current) {
      const $plantSelect = $(plantSelectRef.current);
      if ($plantSelect.data("select2")) {
        $plantSelect.select2("destroy");
      }
      $plantSelect.select2({
        width: "100%",
        placeholder: "Select Plant",
        allowClear: false,
      });
      $plantSelect.on("select2:select", function (e) {
        handleChange("plantCode", e.params.data.id);
      });
      if (product.plantCode) {
        $plantSelect.val(product.plantCode).trigger("change");
      }
    }

    // Cleanup
    return () => {
      if (categorySelectRef.current) {
        const $select = $(categorySelectRef.current);
        if ($select.data("select2")) {
          $select.off("select2:select");
          $select.select2("destroy");
        }
      }
      if (bvSelectRef.current) {
        const $select = $(bvSelectRef.current);
        if ($select.data("select2")) {
          $select.off("select2:select");
          $select.select2("destroy");
        }
      }
      if (plantSelectRef.current) {
        const $select = $(plantSelectRef.current);
        if ($select.data("select2")) {
          $select.off("select2:select");
          $select.select2("destroy");
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dropdowns.categories,
    dropdowns.businessVerticals,
    dropdowns.plants,
  ]);


  // Update Select2 values when product state changes
  useEffect(() => {
    if (categorySelectRef.current && dropdowns.categories.length > 0) {
      const $categorySelect = $(categorySelectRef.current);
      if ($categorySelect.data("select2")) {
        const currentVal = $categorySelect.val();
        if (currentVal !== (product.catgCode || "")) {
          $categorySelect.val(product.catgCode || "").trigger("change");
        }
      }
    }
    if (bvSelectRef.current && dropdowns.businessVerticals.length > 0) {
      const $bvSelect = $(bvSelectRef.current);
      if ($bvSelect.data("select2")) {
        const currentVal = $bvSelect.val();
        if (currentVal !== (product.bvCode || "")) {
          $bvSelect.val(product.bvCode || "").trigger("change");
        }
      }
    }
    if (plantSelectRef.current && dropdowns.plants.length > 0) {
      const $plantSelect = $(plantSelectRef.current);
      if ($plantSelect.data("select2")) {
        const currentVal = $plantSelect.val();
        if (currentVal !== (product.plantCode || "")) {
          $plantSelect.val(product.plantCode || "").trigger("change");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    product.catgCode,
    product.bvCode,
    product.plantCode,
  ]);

  const loadDropdowns = async () => {
    try {
      setLoading(true);
      const [catRes, bvRes, plantRes] = await Promise.all([
        productMasterAPI.getCategories(true),
        masterDataAPI.getVerticals(),
        masterDataAPI.getPlants(),
      ]);

      setDropdowns({
        categories: (catRes?.data || []).map((c) => normalizeCategory(c)),
        businessVerticals: (bvRes?.data || []).map((b) => normalizeBV(b)),
        plants: (plantRes?.data || []).map((p) => normalizePlant(p)),
      });
    } catch (e) {
      console.error("Error loading dropdowns:", e);
      setError("Failed to load dropdown data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const loadNextProductCode = async () => {
    try {
      const response = await productMasterAPI.getNextCode();
      if (response && response.success && response.data) {
        setProduct((prev) => ({ ...prev, productCode: response.data }));
      }
    } catch (e) {
      console.error("Error loading next product code:", e);
      // Fallback to P00001 if API fails
      setProduct((prev) => ({ ...prev, productCode: "P00001" }));
    }
  };

  const loadProduct = async () => {
    try {
      setLoading(true);
      const response = await productMasterAPI.getByCode(productCode);
      if (response && response.success && response.data) {
        setProduct(normalizeProduct(response.data));
      } else {
        navigate("/products");
      }
    } catch (e) {
      console.error("Error loading product:", e);
      if (e.response?.status !== 404) {
        Swal.fire("Error", "Failed to load product details", "error");
      }
      navigate("/products");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setProduct((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!product.productCode?.trim()) {
      Swal.fire("Error", "Product Code is required", "error");
      return false;
    }
    if (!product.productName?.trim()) {
      Swal.fire("Error", "Product Name is required", "error");
      return false;
    }
    if (!product.catgCode) {
      Swal.fire("Error", "Category is required", "error");
      return false;
    }
    if (!product.bvCode) {
      Swal.fire("Error", "Product Vertical is required", "error");
      return false;
    }
    if (!product.plantCode) {
      Swal.fire("Error", "Plant is required", "error");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const createdBy = currentUser?.userIdCode || "SYSADMIN001";
      let response;

      if (isEditMode) {
        response = await productMasterAPI.update(
          productCode,
          product,
          createdBy
        );
      } else {
        response = await productMasterAPI.create(product, createdBy);
      }

      if (response && response.success) {
        Swal.fire({
          title: "Success",
          text: response.message || "Product saved successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/products");
        });
      } else {
        Swal.fire(
          "Error",
          response.message || "Failed to save product",
          "error"
        );
      }
    } catch (e) {
      console.error("Error saving product:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message ||
          "Failed to save product. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="add-edit-product-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className="add-edit-product-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-product-breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/products"
            onClick={(e) => {
              e.preventDefault();
              navigate("/products");
            }}
            className="breadcrumb-link brdcrmbLstitms"
          >
            Product Master
          </Link>
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {isEditMode ? "Edit Product" : "Add Product"}
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-product-header-right">
          <Button
            className="add-edit-product-back-button"
            onClick={() => navigate("/products")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="add-edit-product-error-alert"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <div className="container-fluid add-edit-product-page">
        {/* Basic Information Section */}
        <div className="card-panel mb-3">
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-lg-4">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                  Category <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  ref={categorySelectRef}
                  className="form-control select2-category"
                  required
                >
                  <option value="">Select Category</option>
                  {dropdowns.categories.map((c) => (
                    <option key={c.catgCode} value={c.catgCode}>
                      {c.catgName} ({c.catgCode})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                  Product Vertical <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  ref={bvSelectRef}
                  className="form-control select2-business-vertical"
                  required
                >
                  <option value="">Select Product Vertical</option>
                  {dropdowns.businessVerticals.map((bv) => (
                    <option key={bv.bvCode} value={bv.bvCode}>
                      {bv.bvName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                  Plant <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  ref={plantSelectRef}
                  className="form-control select2-plant"
                  required
                >
                  <option value="">Select Plant</option>
                  {dropdowns.plants.map((p) => (
                    <option key={p.plantCode} value={p.plantCode}>
                      {p.plantName} ({p.plantCode})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Product Code <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={product.productCode}
                  required
                  fullWidth
                  disabled={true}
                  size="small"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Product ID
                </label>
                <TextField
                  value={product.productId}
                  onChange={(e) => handleChange("productId", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter product ID (optional)"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Product Name <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={product.productName}
                  onChange={(e) => handleChange("productName", e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter product name"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mt-4">
          <div className="col-12">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => navigate("/products")}
                className="btn-cancel-product funbtns"
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !canSave}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                className="btn-save-product funbtns"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default AddEditProductMaster;
